import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useEffect} from 'react';
import {Box, Stack, styled} from '@mui/material';
import {
  designSystemToken,
  Headline,
  IconName,
  Label,
  Tooltip,
} from '@lightricks/react-design-system';
import RecentProjects from '@contentlab/components/feature-editor/components/recent-projects';
import {HOME_CONTENT_MAX_WIDTH} from '@contentlab/config/constants';
import {ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useNavigateToPendingDraftProject from '@contentlab/hooks/use-navigate-to-pending-draft-project';
import useOnboardingFlags from '@contentlab/hooks/use-onboarding-flags/useOnboardingFlags';
import useSetOnboardingFlags from '@contentlab/hooks/use-set-onboarding-flags/useSetOnboardingFlags';
import {ONBOARDING_FLAGS} from '@contentlab/types/onBoardingFlags';
import {FeatureName} from '@contentlab/types/project';
import {
  FeatureState,
  getFeatureState,
  ContentLabFlags,
} from '@contentlab/utils/featureUtils';
import getFeatureIconName from '@contentlab/utils/getFeatureIconName';
import FEATURE_ASSETS from '@contentlab/views/contentlab/constants';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import queryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import Link from '@/components/link';
import {CONTENT_LAB, FOLDERS} from '@/hooks/content-studio/consts';
import FeatureCard from './feature-card';

const FeaturesContainer = styled(Box)(() => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  alignItems: 'center',
  width: '100%',
  maxWidth: `${HOME_CONTENT_MAX_WIDTH}px`,
}));

const TopRowContainer = styled(Box)(() => ({
  display: 'flex',
  gap: '24px',
  width: '100%',
  height: '269px',
}));

const BottomRowContainer = styled(Box)(() => ({
  display: 'flex',
  gap: '24px',
  width: '100%',
  height: '246px',
}));

type Feature = FeatureState & {
  id: FeatureName;
  title: string;
  iconName: IconName;
  videoPath: string;
  thumbnailPath: string;
  path: string;
};

const getContentLabFeatures = (flags: ContentLabFlags): Feature[] => {
  const reformattingState = getFeatureState(FeatureName.REFORMATTING, flags);
  const aiDubbingState = getFeatureState(FeatureName.AI_DUBBING, flags);
  const rephrasingState = getFeatureState(FeatureName.REPHRASING, flags);
  const captionsState = getFeatureState(FeatureName.CAPTIONS, flags);
  // TODO: Uncomment when Upscale feature is ready
  const aiAvatarState = getFeatureState(FeatureName.AI_AVATAR, flags);
  // const upscaleState = getFeatureState(FeatureName.UPSCALE, flags);

  const features: Feature[] = [
    {
      id: FeatureName.REFORMATTING,
      title: translate('Resize for Any Ratio'),
      iconName: getFeatureIconName(FeatureName.REFORMATTING),
      videoPath: FEATURE_ASSETS.reformatting.VIDEO,
      thumbnailPath: FEATURE_ASSETS.reformatting.IMAGE,
      path: `${ROUTES.CONTENT_LAB_RESIZE}`,
      ...reformattingState,
    },
    {
      id: FeatureName.AI_DUBBING,
      title: translate('Dub into Any Language'),
      iconName: getFeatureIconName(FeatureName.AI_DUBBING),
      videoPath: FEATURE_ASSETS.ai_dubbing.VIDEO,
      thumbnailPath: FEATURE_ASSETS.ai_dubbing.IMAGE,
      path: `${ROUTES.CONTENT_LAB_DUB}`,
      ...aiDubbingState,
    },
    {
      id: FeatureName.REPHRASING,
      title: translate('Rescript Dialogue'),
      iconName: getFeatureIconName(FeatureName.REPHRASING),
      videoPath: FEATURE_ASSETS.rephrasing.VIDEO,
      thumbnailPath: FEATURE_ASSETS.rephrasing.IMAGE,
      path: `${ROUTES.CONTENT_LAB_REPHRASE}`,
      ...rephrasingState,
    },
  ];

  if (flags.clCaptions) {
    features.push({
      id: FeatureName.CAPTIONS,
      title: translate('Add Captions'),
      iconName: getFeatureIconName(FeatureName.CAPTIONS),
      videoPath: FEATURE_ASSETS.captions.VIDEO,
      thumbnailPath: FEATURE_ASSETS.captions.IMAGE,
      path: `${ROUTES.CONTENT_LAB_CAPTIONS}`,
      ...captionsState,
    });
  }

  if (flags.clAiAvatar) {
    features.push({
      id: FeatureName.AI_AVATAR,
      title: translate('Create an Avatar'),
      iconName: getFeatureIconName(FeatureName.AI_AVATAR),
      videoPath: FEATURE_ASSETS.ai_avatar.VIDEO,
      thumbnailPath: FEATURE_ASSETS.ai_avatar.IMAGE,
      path: `${ROUTES.CONTENT_LAB_AI_AVATAR}`,
      ...aiAvatarState,
    });
  }

  // TODO: Uncomment when Upscale feature is ready + FEATURE_ASSETS.upscale is ready
  // if (flags.clUpscale) {
  //   features.push({
  //     id: FeatureName.UPSCALE,
  //     title: translate('Upscale'),
  //     iconName: getFeatureIconName(FeatureName.UPSCALE),
  //     videoPath: FEATURE_ASSETS.upscale.VIDEO,
  //     thumbnailPath: FEATURE_ASSETS.upscale.IMAGE,
  //     path: `${ROUTES.CONTENT_LAB_UPSCALE}`,
  //     ...upscaleState,
  //   });
  // }

  return features;
};

function FeaturesSection({features}: {features: Feature[]}) {
  const {uiInteraction} = useUiInteractionEvent();

  const renderFeatureCard = (feature: Feature, cardHeight: number) => {
    const featureCard = (
      <FeatureCard
        key={feature.id}
        title={translate(feature.title)}
        iconName={feature.iconName}
        videoPath={feature.videoPath}
        thumbnailPath={feature.thumbnailPath}
        disabled={feature.disabled}
        cardHeight={cardHeight}
      />
    );

    if (feature.disabled) {
      return (
        <Tooltip
          key={feature.id}
          title={feature.tooltip || ''}
          arrow
          type="light"
          placement="top"
          sx={{
            maxWidth: '200px',
            padding: '8px 12px',
            textAlign: 'center',
            fontSize: '14px',
            flex: 1,
          }}
        >
          <div style={{flex: 1}}>{featureCard}</div>
        </Tooltip>
      );
    }

    return (
      <Link
        key={feature.id}
        to={`${ROUTES.CONTENT_LAB}${feature.path}`}
        onClick={() => {
          uiInteraction({
            item_name: `${feature.title}_card`,
            interaction_location: CONTENT_LAB_VIEW_NAMES.HOME,
          });
        }}
        style={{flex: 1}}
      >
        {featureCard}
      </Link>
    );
  };

  // Split features: first 2 in top row, rest in bottom row
  const topRowFeatures = features.slice(0, 2);
  const bottomRowFeatures = features.slice(2);

  return (
    <FeaturesContainer>
      <TopRowContainer>
        {topRowFeatures.map((feature) => renderFeatureCard(feature, 272))}
      </TopRowContainer>
      <BottomRowContainer>
        {bottomRowFeatures.map((feature) => renderFeatureCard(feature, 246))}
      </BottomRowContainer>
    </FeaturesContainer>
  );
}

const MainContainer = styled(Stack)(() => ({
  width: '100%',
  height: 'fit-content',
  overflowY: 'auto',
  alignItems: 'center',
  paddingTop: '8px',
  paddingBottom: '40px',
}));

const ContentWrapper = styled(Stack)(() => ({
  width: 'fit-content',
  alignItems: 'center',
}));

const HeaderContainer = styled(Stack)(() => ({
  width: '100%',
  alignItems: 'center',
  marginBottom: '40px',
}));

const ProjectsHeaderContainer = styled(Stack)(() => ({
  width: '100%',
  justifyContent: 'space-between',
  marginTop: '32px',
  marginBottom: '24px',
}));

const ProjectsTitle = styled(Headline)(() => ({
  textAlign: 'start',
}));

function Home() {
  const {uiInteraction} = useUiInteractionEvent();
  const flags = useFlags();
  useNavigateToPendingDraftProject();
  const {
    hasCompletedCurrentOnboardingVersion,
    hasSeenCurrentPricingPageVersion,
  } = useOnboardingFlags();
  const setHasSeenCurrentPricingPageVersion = useSetOnboardingFlags(
    ONBOARDING_FLAGS.HAS_SEEN_CURRENT_PRICING_PAGE_VERSION
  );
  useEffect(() => {
    if (
      hasCompletedCurrentOnboardingVersion &&
      !hasSeenCurrentPricingPageVersion
    ) {
      setHasSeenCurrentPricingPageVersion();
    }
    queryClient.invalidateQueries({
      queryKey: [CONTENT_LAB, FOLDERS],
    });
  }, []);

  return (
    <MainContainer direction="column">
      <ContentWrapper direction="column">
        <HeaderContainer direction="column">
          <Headline size="xl">
            {translate('Scale your videos with AI-powered tools')}
          </Headline>
        </HeaderContainer>
        <FeaturesSection features={getContentLabFeatures(flags)} />
        <ProjectsHeaderContainer direction="row">
          <ProjectsTitle
            size="md"
            color={designSystemToken('semantic.fg.primary')}
          >
            {translate('Recent Projects')}
          </ProjectsTitle>
          <Link
            to={`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECTS}`}
            onClick={() => {
              uiInteraction({
                item_name: 'see_all_projects',
                interaction_location: CONTENT_LAB_VIEW_NAMES.HOME,
              });
            }}
          >
            <Label size="md" color={designSystemToken('semantic.fg.primary')}>
              {translate('See all')}
            </Label>
          </Link>
        </ProjectsHeaderContainer>
        <RecentProjects projectsCount={3} />
      </ContentWrapper>
    </MainContainer>
  );
}

export default Home;
